import { useState, useEffect, useCallback } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Storage } from '../storage';
import Icon from '../components/Icon';
import Modal from '../components/Modal';

// ─── COLLECTION PAGE ──────────────────────────────────────────────────────────
//
//  Public page rendered when a user navigates to /collections/:id.
//  Owner controls (edit, add timelines, drag-to-reorder, remove) appear when
//  currentUser.id matches collection.userId.
//

// ── Sortable timeline card ────────────────────────────────────────────────────

const SortableCard = ({ tl, isOwner, onRemove, removingId }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tl.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity:   isDragging ? 0.5 : 1,
    cursor:    isDragging ? 'grabbing' : 'default',
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        background:    'var(--surface)',
        border:        '1px solid var(--border)',
        borderRadius:  'var(--radius-xl)',
        padding:       '14px 16px',
        display:       'flex',
        alignItems:    'center',
        gap:           14,
      }}
    >
      {isOwner && (
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab', color: 'var(--text3)', flexShrink: 0,
            display: 'flex', alignItems: 'center', padding: '0 2px',
            touchAction: 'none',
          }}
          aria-label="Drag to reorder"
        >
          <Icon name="grip" size={16} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <a
          href={`/timeline/${tl.id}`}
          style={{
            fontWeight: 600, fontSize: 15, color: 'var(--accent)',
            textDecoration: 'none', display: 'block', marginBottom: 2,
          }}
        >
          {tl.title}
        </a>
        <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
          by {tl.authorName}
          {tl.category && <span style={{ marginLeft: 8, padding: '1px 6px', background: 'var(--bg2)', borderRadius: 4 }}>{tl.category}</span>}
        </p>
      </div>

      {isOwner && (
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => onRemove(tl.id)}
          disabled={removingId === tl.id}
          style={{ color: 'var(--danger)', borderColor: 'rgba(201,88,88,0.3)', flexShrink: 0, opacity: removingId === tl.id ? 0.5 : 1 }}
        >
          {removingId === tl.id ? '…' : 'Remove'}
        </button>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const CollectionPage = ({ collectionId, currentUser }) => {
  const [collection,    setCollection]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [timelines,     setTimelines]     = useState([]);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle,     setEditTitle]     = useState('');
  const [editDesc,      setEditDesc]      = useState('');
  const [editSaving,    setEditSaving]    = useState(false);
  const [editError,     setEditError]     = useState('');

  // Add timeline modal
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [addSearch,     setAddSearch]     = useState('');
  const [addResults,    setAddResults]    = useState([]);
  const [addLoading,    setAddLoading]    = useState(false);
  const [addingId,      setAddingId]      = useState(null);

  // Remove / drag state
  const [removingId,    setRemovingId]    = useState(null);
  const [reordering,    setReordering]    = useState(false);

  const isOwner = !!(currentUser && collection?.isOwner);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ── Load collection ───────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    Storage.getCollection(collectionId)
      .then(({ collection: coll }) => {
        setCollection(coll);
        setTimelines(coll.timelines ?? []);
      })
      .catch(err => setError(err.message || 'Collection not found'))
      .finally(() => setLoading(false));
  }, [collectionId]);

  // ── Edit collection ───────────────────────────────────────────────────────

  const handleOpenEdit = () => {
    setEditTitle(collection.title);
    setEditDesc(collection.description ?? '');
    setEditError('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) { setEditError('Title is required.'); return; }
    setEditSaving(true);
    try {
      const { collection: updated } = await Storage.updateCollection(collectionId, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
      });
      setCollection(prev => ({ ...prev, ...updated }));
      setShowEditModal(false);
    } catch (err) {
      setEditError(err.message || 'Failed to save.');
    }
    setEditSaving(false);
  };

  // ── Search timelines to add ───────────────────────────────────────────────

  const [ownerTimelines, setOwnerTimelines] = useState(null);

  const handleOpenAdd = async () => {
    setAddSearch('');
    setAddResults([]);
    setShowAddModal(true);
    if (!currentUser || ownerTimelines !== null) return;
    setAddLoading(true);
    try {
      const tls = await Storage.getTimelines(currentUser.id);
      const publicTls = tls.filter(t => t.privacy === 'public');
      setOwnerTimelines(publicTls);
    } catch {
      setOwnerTimelines([]);
    }
    setAddLoading(false);
  };

  const handleAddSearch = useCallback((query) => {
    setAddSearch(query);
    const q = query.trim().toLowerCase();
    if (!q || !ownerTimelines) { setAddResults([]); return; }
    const existing = new Set(timelines.map(t => t.id));
    setAddResults(
      ownerTimelines
        .filter(t => !existing.has(t.id) && t.title.toLowerCase().includes(q))
        .slice(0, 10)
    );
  }, [timelines, ownerTimelines]);

  const handleAddTimeline = async (timelineId) => {
    setAddingId(timelineId);
    try {
      await Storage.addToCollection(collectionId, timelineId);
      // Reload to get fresh timeline data from server
      const { collection: fresh } = await Storage.getCollection(collectionId);
      setCollection(fresh);
      setTimelines(fresh.timelines ?? []);
      // Remove from search results
      setAddResults(prev => prev.filter(t => t.id !== timelineId));
    } catch (err) {
      alert(err.message || 'Failed to add timeline.');
    }
    setAddingId(null);
  };

  // ── Remove timeline ───────────────────────────────────────────────────────

  const handleRemove = async (timelineId) => {
    setRemovingId(timelineId);
    try {
      await Storage.removeFromCollection(collectionId, timelineId);
      setTimelines(prev => prev.filter(t => t.id !== timelineId));
      setCollection(prev => ({ ...prev, timelineCount: (prev.timelineCount ?? 1) - 1 }));
    } catch (err) {
      alert(err.message || 'Failed to remove.');
    }
    setRemovingId(null);
  };

  // ── Drag-to-reorder ───────────────────────────────────────────────────────

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = timelines.findIndex(t => t.id === active.id);
    const newIndex = timelines.findIndex(t => t.id === over.id);
    const reordered = arrayMove(timelines, oldIndex, newIndex);
    setTimelines(reordered);
    setReordering(true);
    try {
      await Storage.reorderCollection(collectionId, reordered.map(t => t.id));
    } catch {
      // Revert on failure
      setTimelines(timelines);
    }
    setReordering(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text3)', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">🔍</div>
          <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--accent)', marginBottom: 12 }}>Collection Not Found</h1>
          <p style={{ color: 'var(--text2)', marginBottom: 24 }}>{error || 'This collection does not exist or has been removed.'}</p>
          <a href="/" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block', padding: '10px 24px' }}>Go Home</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Nav ── */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 64,
      }}>
        <div style={{ fontSize: 22, marginRight: 4 }} aria-hidden="true">⏱</div>
        <a href="/" style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)', textDecoration: 'none', flex: 1 }}>
          Chronos
        </a>
        {currentUser && (
          <a href="/" className="btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <Icon name="back" size={14} /> Dashboard
          </a>
        )}
      </nav>

      {/* ── Content ── */}
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                fontFamily: 'var(--font-display)', fontSize: 32,
                color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.02em',
              }}>
                {collection.title}
              </h1>
              {collection.description && (
                <p style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 8 }}>
                  {collection.description}
                </p>
              )}
              {collection.owner && (
                <p style={{ fontSize: 13, color: 'var(--text3)' }}>
                  by <strong style={{ color: 'var(--text2)' }}>{collection.owner.name}</strong>
                  {' · '}{timelines.length} timeline{timelines.length !== 1 ? 's' : ''}
                  {reordering && <span style={{ marginLeft: 8, color: 'var(--text3)' }}>Saving order…</span>}
                </p>
              )}
            </div>

            {isOwner && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={handleOpenEdit}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Icon name="pencil" size={13} /> Edit
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={handleOpenAdd}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Icon name="plus" size={13} /> Add Timelines
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Timeline list */}
        {timelines.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--text3)' }}>
            <div style={{ opacity: 0.3, display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <Icon name="layers" size={40} />
            </div>
            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>No timelines yet</p>
            {isOwner && (
              <p style={{ fontSize: 13 }}>
                Click <strong>Add Timelines</strong> to add your public timelines to this collection.
              </p>
            )}
          </div>
        ) : isOwner ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={timelines.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {timelines.map(tl => (
                  <SortableCard
                    key={tl.id}
                    tl={tl}
                    isOwner={isOwner}
                    onRemove={handleRemove}
                    removingId={removingId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={() => {}}>
            <SortableContext items={timelines.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {timelines.map(tl => (
                  <SortableCard
                    key={tl.id}
                    tl={tl}
                    isOwner={false}
                    onRemove={handleRemove}
                    removingId={removingId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

      </main>

      {/* ── Edit collection modal ── */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Collection" width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="edit-coll-title">Title</label>
            <input
              id="edit-coll-title"
              value={editTitle}
              onChange={e => { setEditTitle(e.target.value.slice(0, 200)); setEditError(''); }}
              maxLength={200}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="edit-coll-desc">Description <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              id="edit-coll-desc"
              value={editDesc}
              onChange={e => setEditDesc(e.target.value.slice(0, 500))}
              rows={3}
              maxLength={500}
              style={{ resize: 'vertical' }}
            />
          </div>
          {editError && (
            <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{editError}</p>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={() => setShowEditModal(false)} disabled={editSaving}>Cancel</button>
            <button
              className="btn-primary"
              onClick={handleSaveEdit}
              disabled={editSaving || !editTitle.trim()}
              style={{ opacity: !editTitle.trim() ? 0.5 : 1 }}
            >
              {editSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Add timelines modal ── */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Add Timelines" width={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input
            value={addSearch}
            onChange={e => handleAddSearch(e.target.value)}
            placeholder="Filter your public timelines…"
            autoFocus
          />

          {addLoading && (
            <p style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>Loading your timelines…</p>
          )}

          {(() => {
            const existing = new Set(timelines.map(t => t.id));
            const displayList = addSearch.trim()
              ? addResults
              : (ownerTimelines ?? []).filter(t => !existing.has(t.id));

            if (!addLoading && ownerTimelines !== null && displayList.length === 0) {
              return (
                <p style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>
                  {addSearch.trim() ? 'No matches.' : 'No more public timelines to add.'}
                </p>
              );
            }

            return displayList.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {displayList.map(tl => (
                  <div key={tl.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', background: 'var(--bg2)',
                    borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', margin: '0 0 2px' }}>{tl.title}</p>
                      <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
                        {tl.category && <span style={{ marginRight: 8 }}>{tl.category}</span>}
                        {tl.orientation}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={() => handleAddTimeline(tl.id)}
                      disabled={addingId === tl.id}
                      style={{ flexShrink: 0, opacity: addingId === tl.id ? 0.6 : 1 }}
                    >
                      {addingId === tl.id ? 'Adding…' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            ) : null;
          })()}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={() => setShowAddModal(false)}>Done</button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default CollectionPage;
